import {
  useCustomMutation,
  useInvalidate,
  useOne,
  useTranslate,
} from "@refinedev/core";
import { Dropdown, Flex, GlobalToken, message, Space, Typography } from "antd";
import { useRef, useState, useEffect, useMemo } from "react";
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from "react-resizable-panels";
import {
  EyeIcon,
  PencilSimpleLineIcon,
  SpeedometerIcon,
  TextAaIcon,
  TimerIcon,
} from "@phosphor-icons/react";

import { useAntTheme } from "hooks/useAntTheme";
import { StoryboardEditor } from "./StoryboardEditor";
import {
  MediaDocument,
  MediaProjectResponse,
  StoryboardHighlights,
} from "pages/media/types";
import { DisplayMode } from "./editor/types";
import {
  CopyOutlined,
  DownOutlined,
  Loading3QuartersOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { getDirectionStyle } from "helpers/utils";
import { timePretty } from "./editor/helpers";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { StoryboardMetrics } from "./editor/StoryboardMetrics";
import { useVolatileBoundStore } from "store";
import { useClipboard } from "hooks/useClipboard";
import { useFeature } from "hooks/useFeature";
import { useSaveStoryboard } from "./hooks/useSaveStoryboard";
import { SubPageHeader } from "components/SubPageHeader";
import { useAnalytics } from "hooks/useAnalytics";
import { useAutoSave } from "hooks/useAutoSave";
import { FeatureFlags } from "@/constants";
import { AnalyticsEvents } from "types/analytics";
import {
  PdfHighlight,
  useHighlightsStore,
} from "../animator/useHighlightsStore";
import { useCuepointsStore } from "../animator/useCuepointsStore";
import {
  extractSourcesHighlightsCuepoints,
  loadProjectAsset,
} from "../animation/helpers/helpers";
import { parseScript } from "pages/media/helpers/scriptAnchors";
import { PdfHighlightsViewer } from "components/PdfHighlightsViewer";
import { ShadowButton } from "components/ShadowButton";
import { useProjectNavigate } from "pages/media/projects/hooks/useProjectNavigate";
import { useProjectInstance } from "pages/media/projects/hooks/useProjectInstance";
import { GenerateMediaButton } from "../GenerateMediaButton";
import { calculateEstimatedGenerationTime } from "../storyboardEditor/helpers";
import { useNavigate } from "react-router-dom";
import { useSaveAnimation } from "../animation/hooks/highlights/useSaveAnimation";
import {
  getProjectDetailRoute,
  hasHeyGeniusDocTag,
  isProjectFlashEducation,
  isProjectHeyGenius,
} from "../../projects/helpers/helper";
import { Button, radius } from "@libertify/ui";
import { useProjectPublications } from "pages/media/projects/hooks/useProjectPublications";

dayjs.extend(duration);

const displayModeTrackingMap: Record<string, string> = {
  editing: "Edit",
  viewing: "View",
};
const DEFAULT_DISPLAY_MODE_LIBERTIFY: DisplayMode = "editing";

export const StoryboardStudio = ({
  project,
  instanceId,
  ...rest
}: {
  project?: MediaProjectResponse;
  instanceId: string;
  storyboardId: string;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { copy } = useClipboard();
  const navigate = useNavigate();
  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    DEFAULT_DISPLAY_MODE_LIBERTIFY
  );
  const [saving, setSaving] = useState(false);
  const [storyboardHighlights, setStoryboardHighlights] =
    useState<StoryboardHighlights | null>(null);
  const { trackEvent } = useAnalytics();
  const storyboardSettingsId = rest.storyboardId;
  const { navigateToProject } = useProjectNavigate();
  const { isEnabled: isMetricsEnabled } = useFeature(
    FeatureFlags.StoryboardMetrics
  );

  const projectId = project?.id ?? "";

  const {
    scriptsWordsCount,
    readingTime,
    metrics,
    storyboard,
    updates,
    resetUpdates,
    highlightIdToScroll,
  } = useVolatileBoundStore((state) => ({
    scriptsWordsCount: state.storyboardState.scriptsWordsCount,
    readingTime: state.storyboardState.readingTime,
    metrics: state.storyboardState.metrics,
    storyboard: state.storyboardState.storyboard,
    updates: state.storyboardState.updates,
    resetUpdates: state.resetUpdates,
    highlightIdToScroll: state.storyboardState.highlightIdToScroll,
  }));
  //update is added whenever handleChangeSection function is called
  const { isSaving: isSavingStoryboard } = useAutoSave(
    () => handleSaveStoryboard(),
    updates,
    resetUpdates
  );
  const pdfViewerPanelRef = useRef<ImperativePanelHandle>(null);
  const currentDocument = project?.settings.primary_document_id
    ? project.documents.find(
        (x) => x.id === project.settings.primary_document_id
      )
    : project?.documents[0];

  const { data } = useOne<MediaDocument>({
    resource: `media/documents/${project?.organization_id}`,
    id: currentDocument?.id,
    queryOptions: {
      enabled: Boolean(project?.organization_id) && Boolean(currentDocument),
    },
  });
  const document = data?.data;

  const storyboardSettings = project?.storyboards.find(
    (x) => x.id === storyboardSettingsId
  );

  const language = storyboardSettings?.language;
  const layoutDirection = getDirectionStyle(language);

  const {
    setHighlights,
    setSectionHighlights,
    setCurrentHighlight,
    setHighlightedStoryboard,
  } = useHighlightsStore();

  const {
    setCuepoints,
    updateCount: highlightUpdateCount,
    setUpdateCount: setHighlightUpdateCount,
  } = useCuepointsStore();

  const highlightsAssetPath = project?.documents[0]?.highlights_asset_path;
  useEffect(() => {
    globalThis.history.pushState(null, "", globalThis.location.href);

    const handlePopState = () => {
      // Redirect back button to projects list
      navigate(`/media/projects`, { replace: true });
    };

    globalThis.addEventListener("popstate", handlePopState);

    return () => {
      globalThis.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  useEffect(() => {
    if (!highlightsAssetPath) return;
    const loadHighlights = async () => {
      const data = (await loadProjectAsset(highlightsAssetPath)) as
        | StoryboardHighlights
        | undefined;
      if (!data) return;
      const { sources, highlights, cuepoints } =
        extractSourcesHighlightsCuepoints(data) ?? {};
      if (highlights) {
        setHighlights(highlights);
      }
      if (cuepoints) {
        setCuepoints(cuepoints);
      }
      if (sources) {
        setSectionHighlights(sources);
      }
      setStoryboardHighlights(data);
    };
    loadHighlights().catch((error) =>
      console.error("Error loading sectionHighlights", error)
    );
  }, [highlightsAssetPath, setHighlights, setCuepoints, setSectionHighlights]);

  useEffect(() => {
    setHighlightedStoryboard(storyboard);
  }, [storyboard, setHighlightedStoryboard]);

  const handleClickHighlight = (highlight: PdfHighlight) => {
    setCurrentHighlight(highlight);
  };

  const { storeStoryboard } = useVolatileBoundStore((store) => ({
    storeStoryboard: store.storyboardState.storyboard,
  }));

  const currentStoryboard = storyboardSettings;
  const storyboardId = currentStoryboard?.asset_id;

  const { isInstanceLive } = useProjectPublications(project?.id);
  const publishedMedia = project?.media.find(
    (media) =>
      media.storyboard_settings_id === currentStoryboard?.id &&
      isInstanceLive(media.instance_id)
  );
  const hasPublishedMedia = !!publishedMedia;

  // Get project media for generation
  const projectMedia = project?.media.find((x) => x.instance_id === instanceId);

  // BE stopped auto-marking on Storyboard patches (MED-7168).
  const { markMediaOutOfDate } = useProjectInstance({
    projectId,
    instanceId,
    mediaId: projectMedia?.id ?? undefined,
  });

  const invalidate = useInvalidate();
  const { mutateAsync: approve } = useCustomMutation();
  const { mutateAsync: regenerateMedia } = useCustomMutation();

  const { saveStoryboard } = useSaveStoryboard({
    projectId: projectId,
    organizationId: project?.organization_id ?? "",
    storyboardId: storyboardId ?? undefined,
    storyboard,
    documentId: project?.documents[0]?.id,
  });

  const saveUpdatedData = useSaveAnimation(
    projectId,
    projectMedia?.id ?? "",
    storyboardId ?? ""
  );

  const validateTitles = storyboard?.sections.every(
    (section) => section.topic !== ""
  );
  // Same rule as titles: every chapter must have a non-empty final script
  // before proceeding — an empty chapter must not be saved.
  const validateScripts = storyboard?.sections.every((section) =>
    Boolean(section.script?.content?.trim())
  );

  // Calculate estimated generation time for hey_genius projects
  const estimatedGenerationTime = useMemo(() => {
    if (!storyboard?.sections || !isProjectHeyGenius(project?.type ?? ""))
      return undefined;
    return calculateEstimatedGenerationTime(storyboard.sections);
  }, [storyboard?.sections, project?.type]);

  const handleSaveStoryboard = async () => {
    if (!storyboard || storyboard.sections.length === 0) {
      console.error(
        "Could not save storyboard since it is empty. Storyboard: ",
        storyboard
      );
      return false;
    }
    try {
      setSaving(true);
      if (highlightUpdateCount > 0) {
        await saveUpdatedData(storyboardHighlights ?? undefined);
        setHighlightUpdateCount(0);
      } else {
        // Resolves to false instead of throwing when the write fails, so
        // report that to the caller: `useAutoSave` keeps the pending updates
        // and retries, and the media isn't marked stale for unsaved content.
        if (!(await saveStoryboard())) {
          setSaving(false);
          return false;
        }
        resetUpdates();
      }
      // Both branches are real text edits: here `highlightUpdateCount` only
      // rises when deleted text takes its anchors with it. Tag-only edits
      // live in the animation editor and must not mark (MED-7169).
      // Best-effort: the save already landed, so a failed mark must not fail
      // the save — that would skip `resetUpdates` and re-trigger autosave.
      await markMediaOutOfDate().catch(() => undefined);
      setSaving(false);
      resetUpdates();
      return true;
    } catch (e) {
      setSaving(false);
      console.debug(e);
      return false;
    }
  };

  const getScriptWithTitles = () => {
    return storyboard?.sections.reduce((acc, x) => {
      return `${acc}# ${x.topic}\n${x.script?.content}\n\n`;
    }, "");
  };

  const getScriptWithoutTitles = () => {
    return storyboard?.sections.reduce((acc, x) => {
      return `${acc}${x.script?.content}\n\n`;
    }, "");
  };

  const handleCopyScriptMenuClick = ({ key }: { key: string }) => {
    const textToCopy =
      key === "withTitles" ? getScriptWithTitles() : getScriptWithoutTitles();
    copy(parseScript(textToCopy ?? "").text);
    trackEvent(AnalyticsEvents.StoryboardScriptCopy, {
      project_id: projectId,
      storyboard_copy_type:
        key === "withTitles" ? "With Titles" : "Without Titles",
    });
  };

  const copyScriptItems = [
    {
      label: t("storyboard.editor.Present.copyFullScriptWithoutTitles"),
      key: "withoutTitles",
    },
  ];

  const changeDisplayMode = (mode: DisplayMode, init = false) => {
    if (!init) setDisplayMode(mode);

    trackEvent(AnalyticsEvents.StoryboardViewChange, {
      project_id: projectId,
      storyboard_view: displayModeTrackingMap[mode] ?? mode,
      storyboard_id: storyboardId ?? "",
      storyboard_sections_count: storeStoryboard?.sections.length ?? 0,
      event_trigger: init ? "page_view" : "click",
    });
  };

  useEffect(() => {
    changeDisplayMode(DEFAULT_DISPLAY_MODE_LIBERTIFY, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeEditorView = (mode: DisplayMode) => () => {
    if (updates > 0) {
      handleSaveStoryboard().catch((error) =>
        console.error("Error saving Storyboard", error)
      );
    }
    changeDisplayMode(mode);
  };

  const handleGoToTemplatesBoard = () => {
    navigateToProject({
      projectId: projectId,
      storyboardId: storyboardSettingsId,
      instanceId,
      subRoute: "storyboard/editor",
    });
  };

  const onClickNext = () => {
    if (isProjectHeyGenius(project?.type ?? "")) {
      if (updates > 0) {
        handleSaveStoryboard().catch((error) =>
          console.error("Error saving Storyboard", error)
        );
      }
      return;
    }
    if (!validateTitles) {
      message.error(t("components.storyboard.StoryboardEditor.pleaseAddTitle"));
      return;
    }
    if (!validateScripts) {
      message.error(
        t("components.storyboard.StoryboardEditor.pleaseAddScript")
      );
      return;
    }
    // HG Doc projects are tagged but typed as "new_video", so they land in
    // this branch. They never need the templates editor (voice / avatar only),
    // and the editor 404s on the magic "heygenius" chapter_template_id in prod.
    // Mirror GenerateMediaButton: save script → approve → invalidate → jump
    // to project detail. No media-settings PATCH needed; transitions/brand-kit
    // were seeded in createProject and the backend uses default templates.
    if (hasHeyGeniusDocTag(project?.tags)) {
      void (async () => {
        setSaving(true);
        try {
          if (updates > 0) {
            await saveStoryboard();
          }
          if (projectMedia?.id) {
            // Media already exists: regenerate it instead of re-approving the
            // storyboard.
            await regenerateMedia({
              url: `media/media/${projectMedia.id}/regenerate`,
              method: "post",
              values: {},
              successNotification: {
                type: "success",
                message: t(
                  "projects.components.ProjectMedias.regenerateSuccess"
                ),
              },
            });
          } else {
            // First generation: approve the storyboard
            await approve({
              url: `media/projects/${project?.id}/storyboards/${storyboardSettingsId}/approve`,
              method: "post",
              values: {},
            });
          }
          await invalidate({
            resource: "media/projects",
            id: project?.id,
            invalidates: ["detail"],
          });
          navigate(`/media/projects/${project?.id}`, {
            state: projectMedia?.id ? {} : { fromStoryboardApproval: true },
          });
        } catch (error) {
          console.error(
            "Error generating media for HeyGenius Doc project",
            error
          );
        } finally {
          setSaving(false);
        }
      })();
      return;
    }
    if (updates > 0) {
      setSaving(true);
      saveStoryboard()
        .then(async (saved) => {
          setSaving(false);
          // A failed write resolves to false rather than rejecting. Stay on
          // the storyboard so the edit isn't silently lost — the hook has
          // already shown the error toast.
          if (!saved) return;

          // Not routed through `handleSaveStoryboard` (it skips the storyboard
          // write when highlight updates are pending, which would drop the
          // edit we're saving here), so mark the media ourselves — BE only
          // does it on a Slides patch. Best-effort: the save already landed,
          // so a failed mark must not block navigation.
          await markMediaOutOfDate().catch(() => undefined);
          handleGoToTemplatesBoard();
        })
        .catch((error) => console.error("Error saving Storyboard", error));
    } else {
      handleGoToTemplatesBoard();
    }
  };

  const handleBackClick = async () => {
    // Autosaved already, so there's nothing to "discard" — just flush any
    // pending save and refresh the project cache before navigating back, so
    // the out-of-date widget doesn't show stale data (see MED-6969).
    if (updates > 0) {
      await handleSaveStoryboard();
      await invalidate({
        resource: "media/projects",
        id: projectId,
        invalidates: ["detail"],
      });
    }
    if (isProjectFlashEducation(project?.type ?? "")) {
      navigate(getProjectDetailRoute(projectId, project?.type, instanceId));
    } else {
      navigateToProject({ projectId, instanceId });
    }
  };

  const changeViewButtonLabel =
    displayMode === "editing" ? "viewing" : "editing";

  return (
    <Flex vertical style={{ margin: -24 }}>
      <StyledPanelGroupWrapper>
        <PanelGroup
          autoSaveId="storyboard_container"
          direction="vertical"
          style={{ height: "100vh" }}
        >
          <SubPageHeader
            itemId={project?.id}
            itemTitle={project?.title ?? ""}
            onBack={handleBackClick}
            page="editStoryboard"
            style={{ margin: 0 }}
            source="projects"
          />
          <Panel defaultSize={75}>
            <PanelGroup autoSaveId="storyboard" direction="horizontal">
              <Panel order={1} defaultSize={55} minSize={20}>
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    padding: 15,
                  }}
                >
                  <StoryboardEditor
                    project={project}
                    storyboardId={storyboardId ?? undefined}
                    displayMode={displayMode}
                    style={layoutDirection}
                  />
                </div>
              </Panel>
              <PanelResizeHandle>
                <StyledPanelResizeHandle></StyledPanelResizeHandle>
              </PanelResizeHandle>
              {!isProjectHeyGenius(project?.type ?? "") && (
                <Panel
                  order={3}
                  collapsible
                  ref={pdfViewerPanelRef}
                  defaultSize={30}
                  maxSize={60}
                  style={{ position: "relative" }}
                >
                  {document?.url && (
                    <PdfHighlightsViewer
                      document={document}
                      startScrollTo={highlightIdToScroll ?? ""}
                      onHighlightClick={handleClickHighlight}
                    />
                  )}
                </Panel>
              )}
            </PanelGroup>
          </Panel>
        </PanelGroup>
        <Flex
          vertical
          style={{
            padding: "24px 16px",
            borderLeft: `1px solid ${theme.colorBorder}`,
          }}
          gap={50}
        >
          <Flex vertical gap={16}>
            <Button
              variant="outlined"
              size="m"
              icon={
                displayMode === "editing" ? (
                  <EyeIcon size={24} />
                ) : (
                  <PencilSimpleLineIcon size={24} />
                )
              }
              onClick={onChangeEditorView(changeViewButtonLabel)}
              style={{ width: "fit-content" }}
            >
              {t(
                `components.storyboard.StoryboardStudio.${changeViewButtonLabel}.title`
              )}
            </Button>
            <StyledDropdownButton>
              <Dropdown.Button
                size="large"
                icon={<DownOutlined />}
                menu={{
                  items: copyScriptItems,
                  onClick: handleCopyScriptMenuClick,
                }}
                onClick={() => {
                  handleCopyScriptMenuClick({ key: "withTitles" });
                }}
              >
                <CopyOutlined /> {t("storyboard.editor.Present.copyScript")}
              </Dropdown.Button>
            </StyledDropdownButton>
            {isProjectHeyGenius(project?.type ?? "") ? (
              !hasPublishedMedia && (
                <GenerateMediaButton
                  projectId={projectId}
                  mediaGenerationMode={projectMedia?.media_generation_mode}
                  storyboardId={storyboardId ?? undefined}
                  storyboardSettingsId={storyboardSettingsId}
                  storyboardStatus={storyboardSettings?.status}
                  mediaId={projectMedia?.id ?? undefined}
                  isPublished={isInstanceLive(projectMedia?.instance_id)}
                  onBeforeGenerate={async () => {
                    if (updates > 0) {
                      await handleSaveStoryboard();
                    }
                  }}
                  isStoryboardSaving={isSavingStoryboard || saving}
                  estimatedGenerationTime={estimatedGenerationTime}
                  trackingPage="Storyboard"
                />
              )
            ) : (
              // Live editing is now supported (see MED-6969) — a published
              // project no longer blocks moving on to the Storyboard editor,
              // it just marks the publication stale. Unlike the HeyGenius
              // branch above, this doesn't generate media directly, so there's
              // nothing left to gate on `hasPublishedMedia`.
              <ShadowButton
                type="primary"
                size="large"
                shape="round"
                loading={saving}
                disabled={saving}
                onClick={onClickNext}
              >
                {t("buttons.next")}
              </ShadowButton>
            )}
          </Flex>
          <Flex vertical gap={16}>
            <Space size="middle">
              <TextAaIcon size={25} weight="bold" opacity={0.5} />
              <Typography.Text strong>
                {scriptsWordsCount}{" "}
                {t("components.storyboard.StoryboardEditor.words")}
              </Typography.Text>
            </Space>
            <Space size="middle">
              <TimerIcon size={25} weight="bold" opacity={0.5} />
              <Typography.Text strong>
                {timePretty(readingTime ?? 0)}
              </Typography.Text>
            </Space>
            {isMetricsEnabled && (
              <>
                <Space size="middle">
                  <SpeedometerIcon size={25} weight="bold" opacity={0.5} />
                  <Typography.Text strong>
                    {t("storyboard.editor.StoryboardMetrics.score")}
                  </Typography.Text>
                  {!metrics && <Loading3QuartersOutlined spin />}
                </Space>
                <div style={{ marginTop: -5, marginLeft: 5 }}>
                  {metrics && <StoryboardMetrics metrics={metrics} />}
                </div>
              </>
            )}
          </Flex>
        </Flex>
      </StyledPanelGroupWrapper>
    </Flex>
  );
};

const StyledPanelResizeHandle = styled.div<{
  theme: GlobalToken;
}>`
  width: 1px;
  height: 100%;
  background: ${({ theme }) => theme.colorBorder};
  margin-left: 1px;
  margin-right: 1px;
`;

const StyledPanelGroupWrapper = styled(Flex)<{
  theme: GlobalToken;
}>`
  div[data-resize-handle-active="keyboard"],
  div[data-resize-handle-active="pointer"] {
    width: 3px;
    margin: 0;
    background: ${({ theme }) => theme.colorPrimary};
    > div {
      background: none;
    }
  }
`;

const StyledDropdownButton = styled.div`
  .ant-btn {
    border-radius: ${radius.pill}px;
  }

  .ant-btn-compact-first-item {
    width: 100%;
  }
`;
